import { buildCoachLeaderSessionContinuity } from "@/lib/behavior/coachSessionContinuity";
import { buildInterventionMemory, type InterventionMemory } from "@/lib/behavior/interventionMemory";
import type {
  CoachSessionPrepAgreementConversationRecord,
  CoachSessionPrepAgreementRecord,
  CoachSessionPrepLatestGrowthRecord,
} from "@/hooks/useCoachSessionPrepData";

type RawCoachSessionPrepRecord = {
  id: string;
  leader_id: string | null;
  transcript_upload_id: string | null;
  created_at: string;
  source_type: string;
  worked_on: unknown;
  direction: unknown;
  next_conversation_signals: unknown;
  ambiguity_reasons?: unknown;
  ambiguity_flag?: boolean | null;
  coach_review_v1?: unknown;
  leader_plan_draft_v1?: unknown;
  published_to_leader?: boolean | null;
  coach_note: string | null;
};

export type CoachSessionPrepContinuityViewModel = {
  topSummary: string;
  whatChanged: string[];
  whatCarriesForward: string[];
  whatToProbeNext: string[];
  agreementRecord: CoachSessionPrepAgreementRecord;
  latestCoachGrowthRecord: CoachSessionPrepLatestGrowthRecord;
  interventionMemory: InterventionMemory;
};

function normalizeLine(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function stripMeetingBoilerplate(value: string) {
  return value
    .replace(/^(?:Agreement focus|Coach\/leader signal|Veera context):\s*/i, "")
    .replace(/^In this [^,]+,\s*/i, "")
    .replace(/^During this [^,]+,\s*/i, "")
    .replace(/^For this [^,]+,\s*/i, "")
    .replace(/^The conversation centers on\s*/i, "Conversation centers on ");
}

function firstMeaningfulClause(value: string) {
  const sentenceMatch = value.match(/(.+?[.!?])(\s|$)/);
  const sentence = sentenceMatch?.[1]?.trim() || value;
  const clause = sentence.split(/\s+(?:while|although|but)\s+/i)[0]?.trim() || sentence;
  const cleanedClause = clause.replace(/[,:;]+$/g, "").trim();
  if (sentenceMatch) {
    return cleanedClause.endsWith(".") || cleanedClause.endsWith("!") || cleanedClause.endsWith("?")
      ? cleanedClause
      : `${cleanedClause}.`;
  }
  return cleanedClause;
}

function trimSingleLine(value: string, max = 220) {
  const normalized = normalizeLine(value);
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}...`;
}

export function simplifyContinuityLine(value: string) {
  const normalized = normalizeLine(value);
  if (!normalized) return "";
  const stripped = stripMeetingBoilerplate(normalized);
  const firstClause = firstMeaningfulClause(stripped);
  return trimSingleLine(firstClause, 140);
}

function uniqueLines(values: Array<string | null | undefined>, limit = 4): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = simplifyContinuityLine(value || "");
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }

  return result;
}

function buildTopSummary(params: {
  whatChanged: string[];
  whatCarriesForward: string[];
  whatToProbeNext: string[];
}) {
  const changed = params.whatChanged[0] || null;
  const carryForward = params.whatCarriesForward.find((line) => line !== changed) || params.whatCarriesForward[0] || null;
  const next = params.whatToProbeNext[0] || null;

  if (changed && carryForward && next) {
    return `Across the last few sessions, ${changed} keeps shifting while ${carryForward} carries forward and ${next} stays under probe.`;
  }

  if (changed && carryForward) {
    return `Across the last few sessions, ${changed} keeps shifting while ${carryForward} carries forward.`;
  }

  if (changed && next) {
    return `Across the last few sessions, ${changed} keeps shifting while ${next} stays under probe.`;
  }

  if (carryForward && next) {
    return `Across the last few sessions, ${carryForward} carries forward and ${next} stays under probe.`;
  }

  return changed || carryForward || next || "Continuity will sharpen as more shared evidence accumulates.";
}

export function buildCoachSessionPrepContinuityViewModel(params: {
  rows: RawCoachSessionPrepRecord[];
}) : CoachSessionPrepContinuityViewModel {
  const continuityRows = params.rows.map((row) => ({
    row,
    continuity: buildCoachLeaderSessionContinuity({
      record: row,
    }),
  }));

  const recentRows = continuityRows.slice(0, 3);
  const namedAgreements = Array.from(new Set(
    continuityRows
      .flatMap(({ continuity }) => continuity.leaderDevelopment.discussed)
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      .map(normalizeLine),
  )).slice(0, 6);
  const whatChanged = uniqueLines(
    recentRows.flatMap(({ continuity }) => continuity.leaderDevelopment.discussed),
  );
  const whatCarriesForward = uniqueLines([
    ...namedAgreements,
    ...recentRows.flatMap(({ continuity }) => continuity.leaderDevelopment.nextFocus),
  ], 6);
  const whatToProbeNext = uniqueLines(
    recentRows.flatMap(({ continuity }) => continuity.leaderDevelopment.nextSession),
  );

  const conversationRecords: CoachSessionPrepAgreementConversationRecord[] = continuityRows
    .map(({ row, continuity }) => {
      const bullets = [
        ...continuity.leaderDevelopment.discussed.slice(0, 2),
        ...continuity.leaderDevelopment.nextFocus.slice(0, 1),
        ...continuity.leaderDevelopment.nextSession.slice(0, 1),
      ];
      const continuitySummary = bullets.join(" • ");
      const summary = trimSingleLine(continuitySummary || row.coach_note || "", 220);
      return {
        id: row.id,
        source: row.source_type === "transcript_review" ? "Transcript review" : "Coach Veera",
        created_at: row.created_at,
        transcriptUploadId: row.transcript_upload_id,
        tags: row.source_type === "transcript_review"
          ? ["Transcript-derived", "Saved by coach"]
          : ["Saved by coach"],
        summary,
      };
    })
    .filter((row) => row.summary.length > 0)
    .slice(0, 8);
  const interventionMemory = buildInterventionMemory({
    continuityRows: continuityRows.map(({ continuity }) => ({
      recordId: continuity.recordId,
      createdAt: continuity.createdAt,
      source: continuity.source,
      leaderDevelopment: continuity.leaderDevelopment,
    })),
  });

  const latest = continuityRows[0];

  return {
    topSummary: buildTopSummary({
      whatChanged,
      whatCarriesForward,
      whatToProbeNext,
    }),
    whatChanged,
    whatCarriesForward,
    whatToProbeNext,
    agreementRecord: {
      namedAgreements,
      conversationRecords,
    },
    latestCoachGrowthRecord: latest
      ? {
          source: latest.row.source_type === "transcript_review" ? "Transcript review" : "Coach Veera",
          created_at: latest.row.created_at,
          workedOn: latest.continuity.leaderDevelopment.discussed,
          direction: latest.continuity.leaderDevelopment.nextFocus,
          nextConversationSignals: latest.continuity.leaderDevelopment.nextSession,
          transcriptUploadId: latest.row.transcript_upload_id,
        }
      : null,
    interventionMemory,
  };
}
